// Overview of counts and the work awaiting human review. No fabricated trends —
// only real status counts pulled from the API.

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiListMeta } from '../lib/api.js';
import type { Concept, ImageRequest, ResearchItem } from '../lib/types.js';

function useCount(path: string, key: string) {
  return useQuery({
    queryKey: ['count', key],
    queryFn: async () => {
      const res = (await api.get(`${path}?pageSize=1`)) as { meta?: ApiListMeta };
      return res.meta?.total ?? 0;
    },
  });
}

export function DashboardPage() {
  const researchToReview = useQuery({
    queryKey: ['research', { status: 'draft' }],
    queryFn: () => api.get<ResearchItem[]>('/research?status=draft&pageSize=5'),
  });
  const conceptsToReview = useQuery({
    queryKey: ['concepts', { status: 'needs_review' }],
    queryFn: () => api.get<Concept[]>('/concepts?status=needs_review&pageSize=5'),
  });
  const requestsToReview = useQuery({
    queryKey: ['image-requests', 'submitted_for_review'],
    queryFn: () =>
      api.get<ImageRequest[]>('/image-requests?status=submitted_for_review&pageSize=5'),
  });

  const researchTotal = useCount('/research', 'research');
  const conceptTotal = useCount('/concepts', 'concepts');
  const requestTotal = useCount('/image-requests', 'image-requests');
  const assetTotal = useCount('/assets', 'assets');

  return (
    <section>
      <h1>Dashboard</h1>

      <div className="card-grid">
        <Link className="stat-card" to="/research">
          <span className="stat-num">{researchTotal.data ?? '—'}</span>
          <span>Research items</span>
        </Link>
        <Link className="stat-card" to="/concepts">
          <span className="stat-num">{conceptTotal.data ?? '—'}</span>
          <span>Concepts</span>
        </Link>
        <Link className="stat-card" to="/image-requests">
          <span className="stat-num">{requestTotal.data ?? '—'}</span>
          <span>Image requests</span>
        </Link>
        <Link className="stat-card" to="/assets">
          <span className="stat-num">{assetTotal.data ?? '—'}</span>
          <span>Assets</span>
        </Link>
      </div>

      <h2>Awaiting your review</h2>
      <div className="review-panel">
        <h3>Research drafts ({researchToReview.data?.data.length ?? 0})</h3>
        <ul>
          {researchToReview.data?.data.map((r) => (
            <li key={r.id}>
              <Link to={`/research/${r.id}`}>{r.title}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="review-panel">
        <h3>Concepts needing review ({conceptsToReview.data?.data.length ?? 0})</h3>
        <ul>
          {conceptsToReview.data?.data.map((c) => (
            <li key={c.id}>
              <Link to={`/concepts/${c.id}`}>{c.title}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="review-panel">
        <h3>Image requests to approve ({requestsToReview.data?.data.length ?? 0})</h3>
        <ul>
          {requestsToReview.data?.data.map((r) => (
            <li key={r.id}>
              <Link to="/prompt-review">{r.promptSnapshot.slice(0, 60)}…</Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
